import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { AfterViewInit, Component, OnDestroy, computed, effect, inject, signal, viewChild } from '@angular/core';
import { FormArray, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { ICtaForm, ICustomCtaValue } from '@campaign-form/campaign-form.interface';
import { TCtaTemplateSort } from '@campaign-form/form-fields/creators-and-ctas/creators-and-ctas.interface';
import { CreatorsAndCtasService } from '@campaign-form/form-fields/creators-and-ctas/creators-and-ctas.service';
import { CtaDetailsService } from '@campaign-form/form-fields/creators-and-ctas/cta-details.service';
import { ICtaTemplateApi } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import { CtaSelectService } from '@campaign-form/form-fields/creators-and-ctas/cta-select.service';
import { CtaTemplatePlatformChangeService } from '@campaign-form/form-fields/creators-and-ctas/cta-template-platform-change.service';
import { IDeliverableConfigurationApi } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { IGamePlayDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/game-play/game-play.interface';
import { IExecution } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { IExpectedKpi } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/expected-kpi-step/execution-expected-kpi-list/execution-expected-kpis/expected-kpi-list/expected-kpi/expected-kpi.interface';
import { CustomValuesService } from '@core/services/custom-values.service';
import { EDeliverableType, EOverwriteStatus, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { TGlobalTemplateApi } from '@pages/settings/template-settings/template-settings.interface';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { CustomCreatorBadgeComponent } from '@shared/components/custom-creator-badge/custom-creator-badge.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { ALL_CREATORS_ID, ALL_CREATORS_OPTION } from '@shared/constants/all-creators.constants';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { SignalSetUtilitiesService } from '@utilities/signal-set-utilities.service';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { CreatorSelectMenuComponent } from './creator-select-menu/creator-select-menu.component';
import { CtaNameGeneratorService } from './cta-name-generator.service';
import {
  EDeliverableFlowStep,
  IDeliverableFlowDialogData,
  IDeliverableFlowDialogResult,
  IHeaderStep,
} from './deliverable-flow-dialog.interface';
import { DeliverableFlowDialogService } from './deliverable-flow-dialog.service';
import { DeliverableFlowDialogStore } from './deliverable-flow-dialog.store';
import { DeliverableStepComponent } from './deliverable-step/deliverable-step.component';
import { DeliverableStepService } from './deliverable-step/deliverable-step.service';
import { ExecutionStepComponent } from './execution-step/execution-step.component';
import { ExecutionExpectedKpisService } from './expected-kpi-step/execution-expected-kpi-list/execution-expected-kpis/execution-expected-kpis.service';
import { ExpectedKpiStepComponent } from './expected-kpi-step/expected-kpi-step.component';
import { PlatformStepComponent } from './platform-step/platform-step.component';
import { ReviewStepComponent } from './review-step/review-step.component';

@Component({
  imports: [
    SideDialogComponent,
    TranslateModule,
    CreatorSelectMenuComponent,
    PlatformStepComponent,
    DeliverableStepComponent,
    ExecutionStepComponent,
    ExpectedKpiStepComponent,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    ReviewStepComponent,
    CustomCreatorBadgeComponent,
    MatTooltipModule,
    TextClampTooltipDirective,
  ],
  providers: [DeliverableFlowDialogStore],
  selector: 'app-deliverable-flow-dialog',
  styleUrls: ['./deliverable-flow-dialog.component.scss'],
  templateUrl: './deliverable-flow-dialog.component.html',
})
export class DeliverableFlowDialogComponent implements AfterViewInit, OnDestroy {
  private readonly data = inject<IDeliverableFlowDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<DeliverableFlowDialogComponent>);
  private readonly ctaDetailsService = inject(CtaDetailsService);
  private readonly creatorsAndCtasService = inject(CreatorsAndCtasService);
  private readonly deliverableFlowDialogService = inject(DeliverableFlowDialogService);
  private readonly deliverableFlowDialogStore = inject(DeliverableFlowDialogStore);
  private readonly ctaTemplatePlatformChangeService = inject(CtaTemplatePlatformChangeService);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly ctaNameGeneratorService = inject(CtaNameGeneratorService);
  private readonly deliverableStepService = inject(DeliverableStepService);
  private readonly customValuesService = inject(CustomValuesService);

  public readonly campaignCreatorsValue = computed(() => this.data.campaignCreatorsValue);
  public readonly createMode = computed(() => this.data.createMode ?? true);
  public readonly ctaValues = computed(() => this.data.ctaValues ?? undefined);
  public readonly isTemplateSettings = computed(() => this.data.isTemplateSettings ?? false);
  public readonly startingStep = computed(() => {
    const step = this.data.startingStep ?? EDeliverableFlowStep.platform;

    if (step === EDeliverableFlowStep.execution && !this.data.ctaValues?.deliverables?.length) {
      return EDeliverableFlowStep.deliverable;
    }

    return step;
  });
  public readonly globalTemplates = computed(() => this.deliverableFlowDialogStore.templates$());
  public readonly globalTemplateIds = computed(() => {
    const templates = this.globalTemplates();

    if (!templates) {
      return undefined;
    }

    return new Set<string>(templates.map((template) => template.id));
  });
  public readonly ctasValues = computed(() => {
    const templates = this.globalTemplates();
    const globalTemplates = templates ? this.mapGlobalTemplatesToValues(templates) : [];

    return this.mergeCtaValues(this.data.ctasValues, globalTemplates);
  });

  public readonly selectedPlatform = computed(() =>
    this.selectedPlatformFeature() ? SocialUtilitiesService.getPlatform(this.selectedPlatformFeature()!) : null,
  );
  public readonly initialCreators = computed(() => [
    ...(this.data.creatorIds?.includes(ALL_CREATORS_ID) ? [ALL_CREATORS_OPTION(this.translate)] : []),
    ...(this.campaignCreatorsValue()?.reduce((acc, cc) => {
      if (cc.creator && cc.creatorId && this.data.creatorIds?.includes(cc.creatorId)) {
        acc.push(cc.creator);
      }

      return acc;
    }, [] as ICreator[]) ?? []),
  ]);
  public readonly customModeInitialCreatorId = computed(() => this.data.customModeInitialCreatorId ?? null);

  public readonly step = signal<EDeliverableFlowStep>(this.startingStep());
  public readonly selectedStepIndex = signal<number>(0);
  public readonly highestReachedStepIndex = signal<number>(0);
  public readonly selectedPlatformFeature = signal<EPlatformFeature | null>(null);
  public readonly reachedLastStep = signal<boolean>(false);
  public readonly selectedCreatorIds = signal<Set<string>>(new Set<string>());
  public readonly ctaForm = signal<FormGroup<ICtaForm> | null>(null);
  public readonly deliverableConfigurations = signal<IDeliverableConfigurationApi[] | null>(null);
  public readonly customModeCreatorId = signal<string | TNil>(null);
  public readonly newPlatformAddedCreatorIds = signal<Set<string>>(new Set<string>());
  public readonly executionValues = signal<IExecution[]>([]);
  public readonly gamePlayValues = signal<FormArray<FormGroup<IGamePlayDeliverableForm>>['value']>([]);
  public readonly templateSelectedCreatorIds = signal<Set<string> | null>(null);

  public readonly EDeliverableFlowStep = EDeliverableFlowStep;
  public readonly STEP_INDEXES: Record<EDeliverableFlowStep, number> = {
    [EDeliverableFlowStep.platform]: 0,
    [EDeliverableFlowStep.deliverable]: 1,
    [EDeliverableFlowStep.execution]: 2,
    [EDeliverableFlowStep.kpis]: 3,
    [EDeliverableFlowStep.review]: 4,
  };
  public readonly STEP_BY_INDEX: Record<number, EDeliverableFlowStep> = {
    0: EDeliverableFlowStep.platform,
    1: EDeliverableFlowStep.deliverable,
    2: EDeliverableFlowStep.execution,
    3: EDeliverableFlowStep.kpis,
    4: EDeliverableFlowStep.review,
  };

  public readonly isSaveAndExitDisabled = computed(
    () => this.selectedStepIndex() === 0 && !this.selectedPlatformFeature(),
  );

  public get isContinueDisabled(): boolean {
    switch (this.selectedStepIndex()) {
      case 0:
        return !this.selectedPlatformFeature();
      case 1:
        return !this.ctaForm()?.controls?.deliverables?.getRawValue()?.length;
      case 2:
        return !this.hasRealExecutionsInForm();
      default:
        return false;
    }
  }

  public readonly headerSteps: ReadonlyArray<IHeaderStep> = [
    {
      label: this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.deliverableFlow.headerSteps.type'),
      step: EDeliverableFlowStep.platform,
    },
    {
      label: this.translate.instant(
        'pages.campaignForm.sections.creatorsAndCtas.deliverableFlow.headerSteps.deliverables',
      ),
      step: EDeliverableFlowStep.deliverable,
    },
    {
      label: this.translate.instant(
        'pages.campaignForm.sections.creatorsAndCtas.deliverableFlow.headerSteps.frequency',
      ),
      step: EDeliverableFlowStep.execution,
    },
    {
      label: this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.deliverableFlow.headerSteps.goals'),
      step: EDeliverableFlowStep.kpis,
    },
  ];

  private readonly LAST_STEP_INDEX = 4 as const;
  private readonly initializedFormsForAutoName = new WeakSet<FormGroup<ICtaForm>>();
  private readonly dfStepper = viewChild.required(MatStepper);

  private deliverablesChangesSubscription: Subscription | null = null;
  private executionsChangesSubscription: Subscription | null = null;
  private previousGamePlayDeliverablesCount = 0;
  private previousExecutionsCount = 0;
  private pendingDiscardArmTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.deliverableFlowDialogStore.fetchGlobalTemplates();
    this.setInitialSelectedCreatorIds();
    this.createCustomModeCreatorIdEffect();
    this.createCtaFormAutoNameEffect();
    this.createPlatformFeatureEffect();
    this.createSelectedPlatformFeatureSyncEffect();
    this.createCustomModeInitialEffect();
  }

  public ngAfterViewInit(): void {
    const startingIndex = this.STEP_INDEXES[this.startingStep()];

    this.dfStepper().selectedIndex = startingIndex;
    // Sync signals so header reflects the starting step immediately
    this.selectedStepIndex.set(startingIndex);
    this.step.set(this.startingStep());
    this.updateHighestReachedStepIndex(startingIndex);
    this.checkStepConditions(startingIndex);
  }

  public ngOnDestroy(): void {
    this.deliverablesChangesSubscription?.unsubscribe();
    this.executionsChangesSubscription?.unsubscribe();

    if (this.pendingDiscardArmTimeout !== null) {
      clearTimeout(this.pendingDiscardArmTimeout);
      this.pendingDiscardArmTimeout = null;
    }
  }

  public onCloseClick(): void {
    const result: IDeliverableFlowDialogResult = {
      ctaFormValues: this.deliverableFlowDialogService.getInitialCtaValues(),
      discarded: true,
      newPlatformAddedCreatorIds: Array.from(this.newPlatformAddedCreatorIds()),
    };

    if (this.ctaForm()?.dirty || (this.createMode() && this.selectedPlatformFeature())) {
      this.openConfirmDialog(result);

      return;
    }

    this.closeDialog(result);
  }

  public onHeaderClick(step: EDeliverableFlowStep): void {
    if (!this.isHeaderStepClickable(step)) {
      return;
    }

    this.dfStepper().selectedIndex = this.STEP_INDEXES[step];
  }

  public onSelectionChange(event: StepperSelectionEvent): void {
    if (event.selectedIndex === this.selectedStepIndex()) {
      return;
    }

    this.selectedStepIndex.set(event.selectedIndex);
    this.step.set(this.STEP_BY_INDEX[event.selectedIndex]);
    this.checkStepConditions(event.selectedIndex);
  }

  public onContinueClick(): void {
    const ctaForm = this.ctaForm();

    if (
      !ctaForm ||
      !this.isCtaFormEffectivelyValid(ctaForm) ||
      (this.selectedStepIndex() === this.STEP_INDEXES[EDeliverableFlowStep.deliverable] &&
        !this.hasDeliverablesInForm()) ||
      (this.selectedStepIndex() === this.STEP_INDEXES[EDeliverableFlowStep.execution] &&
        !this.hasRealExecutionsInForm())
    ) {
      ctaForm?.markAllAsTouched();

      return;
    }

    this.dfStepper().next();
  }

  public getStepIndex(step: EDeliverableFlowStep): number {
    return this.STEP_INDEXES[step];
  }

  public isHeaderStepActive(step: EDeliverableFlowStep): boolean {
    return this.selectedStepIndex() === this.getStepIndex(step);
  }

  public isHeaderStepClickable(step: EDeliverableFlowStep): boolean {
    const targetIndex = this.getStepIndex(step);

    if (this.isHeaderStepActive(step)) {
      return true;
    }

    return this.canNavigateTo(targetIndex);
  }

  public isHeaderStepCompleted(step: EDeliverableFlowStep): boolean {
    return this.selectedStepIndex() > this.getStepIndex(step);
  }

  public updateSelectedCreatorIds(creatorIds: Set<string> = new Set<string>()): void {
    SignalSetUtilitiesService.updateSetItems(this.selectedCreatorIds, [], [], creatorIds);

    if (this.customModeCreatorId() && !creatorIds.has(this.customModeCreatorId()!)) {
      this.customModeCreatorId.set(null);
    }
  }

  public onSaveAndExitClick(): void {
    const ctaForm = this.ctaForm();

    if (!ctaForm) {
      return;
    }

    if (!this.isCtaFormEffectivelyValid(ctaForm)) {
      ctaForm.markAllAsTouched();

      return;
    }

    const customModeCreatorId = this.customModeCreatorId();

    this.customModeCreatorId.set(null);
    this.onModeChange(false);

    requestAnimationFrame(() => {
      setTimeout(() => {
        queueMicrotask(() => {
          const ctaFormValues = this.getSanitizedCtaFormValues(ctaForm.getRawValue(), customModeCreatorId);
          const result: IDeliverableFlowDialogResult = {
            ctaFormValues,
            newPlatformAddedCreatorIds: Array.from(this.newPlatformAddedCreatorIds()),
          };

          this.creatorsAndCtasService.updateCampaignCreatorsCtaValues(
            this.campaignCreatorsValue(),
            Array.from(this.selectedCreatorIds()),
            ctaFormValues,
          );

          this.closeDialog(result);
        });
      });
    });
  }

  public onSelectTemplateConfirmed(ctaValues: FormGroup<ICtaForm>['value']): void {
    const creatorIdsWithThisTemplate = this.campaignCreatorsValue().reduce((acc, cc) => {
      if (cc.creatorId && cc.ctas?.some((cta) => cta.id === ctaValues.id)) {
        acc.push(cc.creatorId);
      }

      return acc;
    }, [] as string[]);

    this.checkStepConditions(this.LAST_STEP_INDEX);

    this.ctaForm.set(null);
    this.templateSelectedCreatorIds.set(new Set([...creatorIdsWithThisTemplate, ...this.selectedCreatorIds()]));

    // This used to require setTimeout, but it's not needed anymore
    this.deliverableFlowDialogService.createCtaForm(
      this.selectedPlatformFeature(),
      ctaValues,
      this.ctaForm,
      this.data.creatorIds ?? [],
      this.selectedCreatorIds(),
    );

    this.updateGlobalTemplateValues(ctaValues);
  }

  public onPlatformTemplateSelected(templateId: string): void {
    const selectedTemplate = this.ctasValues().find((ctaValue) => ctaValue.id === templateId);

    if (!selectedTemplate) {
      return;
    }

    this.applySelectedPlatformFeature(selectedTemplate.platformFeature ?? null);
    this.onSelectTemplateConfirmed(selectedTemplate);

    const deliverableStepIndex = this.STEP_INDEXES[EDeliverableFlowStep.deliverable];

    this.dfStepper().selectedIndex = deliverableStepIndex;
  }

  public async onPlatformFeatureChange(platformFeature: EPlatformFeature | null): Promise<void> {
    const ctaForm = this.ctaForm();

    if (!platformFeature) {
      return;
    }

    const currentPlatformFeature = ctaForm?.controls.platformFeature.value ?? this.selectedPlatformFeature();

    if (!ctaForm || !currentPlatformFeature || currentPlatformFeature === platformFeature) {
      this.applySelectedPlatformFeature(platformFeature);

      return;
    }

    if (!ctaForm.controls.deliverables.length && !ctaForm.controls.executions.length) {
      await this.applyConfirmedPlatformChange(ctaForm, currentPlatformFeature, platformFeature);

      return;
    }

    this.openPlatformChangeConfirmDialog(ctaForm, currentPlatformFeature, platformFeature);
  }

  private updateGlobalTemplateValues(ctaValues: FormGroup<ICtaForm>['value']): void {
    if (!this.globalTemplateIds()?.has(ctaValues.id ?? '') || this.isTemplateSettings()) {
      return;
    }

    const idForNewCopy = uuidv4();

    this.ctaForm()?.controls?.id?.setValue(idForNewCopy);
    this.ctaForm()?.controls?.isAllCreators?.setValue(this.selectedCreatorIds().has(ALL_CREATORS_ID), {
      emitEvent: false,
    });
    this.deliverableStepService.ensureCtaNameIsUnique(this.ctaForm()!, this.data.ctasValues ?? []);
    this.deliverableFlowDialogService.normalizeGlobalTemplateTimings(this.ctaForm());
  }

  private canNavigateTo(targetIndex: number): boolean {
    if (targetIndex < this.selectedStepIndex()) {
      return true;
    }

    const ctaForm = this.ctaForm();

    if (ctaForm && !this.isCtaFormEffectivelyValid(ctaForm)) {
      return false;
    }

    return targetIndex <= this.getHighestReachableStepIndex();
  }

  private getHighestReachableStepIndex(): number {
    const ctaForm = this.ctaForm();

    if (!ctaForm || !this.isCtaFormEffectivelyValid(ctaForm) || !this.selectedPlatformFeature()) {
      return this.STEP_INDEXES[EDeliverableFlowStep.platform];
    }

    if (!this.hasDeliverablesInForm()) {
      return this.STEP_INDEXES[EDeliverableFlowStep.deliverable];
    }

    if (!this.hasRealExecutionsInForm()) {
      return this.STEP_INDEXES[EDeliverableFlowStep.execution];
    }

    return this.LAST_STEP_INDEX;
  }

  private checkStepConditions(index: number): void {
    this.setExecutionValues();
    this.setGamePlayValues();
    this.checkIfLastStep(index);
    this.updateHighestReachedStepIndex(index);
  }

  private checkIfLastStep(index: number): void {
    if (index === this.LAST_STEP_INDEX) {
      this.reachedLastStep.set(true);
    }
  }

  private updateHighestReachedStepIndex(index: number): void {
    this.highestReachedStepIndex.set(Math.max(this.highestReachedStepIndex(), index));
  }

  private setInitialSelectedCreatorIds(): void {
    effect(() => {
      this.selectedCreatorIds.set(new Set(this.initialCreators()?.map((creator) => creator.id) ?? []));
    });
  }

  private createCustomModeCreatorIdEffect(): void {
    effect(() => {
      const customModeCreatorId = this.customModeCreatorId();

      this.onModeChange(!!customModeCreatorId);
    });
  }

  public onModeChange(isCustomMode: boolean): void {
    const ctaForm = this.ctaForm();
    ctaForm?.markAllAsTouched();
    this.ctaDetailsService.clearCustomValueCache();

    if (!ctaForm || !this.isCtaFormEffectivelyValid(ctaForm)) {
      return;
    }

    setTimeout(() => {
      queueMicrotask(() => {
        ctaForm?.get('isCustomMode')?.setValue(isCustomMode, { emitEvent: false });
        this.setExecutionValues();
        this.setGamePlayValues();
      });
    });
  }

  private createPlatformFeatureEffect(): void {
    effect(() => {
      this.deliverableFlowDialogService.createCtaForm(
        this.selectedPlatformFeature(),
        this.ctaValues(),
        this.ctaForm,
        this.data.creatorIds ?? [],
        this.selectedCreatorIds(),
      );
    });
  }

  private createSelectedPlatformFeatureSyncEffect(): void {
    effect(() => {
      const ctaForm = this.ctaForm();
      const selectedPlatformFeature = this.selectedPlatformFeature();
      const formPlatformFeature = ctaForm?.controls.platformFeature.value ?? null;

      if (!ctaForm || selectedPlatformFeature || !formPlatformFeature) {
        return;
      }

      this.selectedPlatformFeature.set(formPlatformFeature);
    });
  }

  private createCtaFormAutoNameEffect(): void {
    effect(() => {
      const ctaForm = this.ctaForm();

      this.deliverablesChangesSubscription?.unsubscribe();
      this.deliverablesChangesSubscription = null;
      this.executionsChangesSubscription?.unsubscribe();
      this.executionsChangesSubscription = null;

      if (!ctaForm) {
        return;
      }

      this.ctaNameGeneratorService.clearManualOverride(ctaForm);

      if (!this.initializedFormsForAutoName.has(ctaForm) && this.shouldApplyInitialContentName()) {
        this.ctaNameGeneratorService.applyInitialContentName(ctaForm, this.data.ctasValues ?? []);
      }

      const selectedPlatformFeature = this.selectedPlatformFeature();

      if (
        !this.initializedFormsForAutoName.has(ctaForm) &&
        selectedPlatformFeature &&
        this.shouldApplyInitialContentName()
      ) {
        this.ctaNameGeneratorService.applyPlatformFeatureName(
          ctaForm,
          this.data.ctasValues ?? [],
          selectedPlatformFeature,
        );
      }

      this.initializedFormsForAutoName.add(ctaForm);
      this.previousGamePlayDeliverablesCount = this.getGamePlayDeliverablesCount(ctaForm);
      this.previousExecutionsCount = ctaForm.controls.executions.length;
      this.deliverablesChangesSubscription = ctaForm.controls.deliverables.valueChanges.subscribe(() => {
        this.handleDeliverablesChanged(ctaForm);
      });
      this.executionsChangesSubscription = ctaForm.controls.executions.valueChanges.subscribe(() => {
        this.handleExecutionsChanged(ctaForm);
      });
    });
  }

  private createCustomModeInitialEffect(): void {
    effect(() => {
      const selectedCreatorId = this.customModeInitialCreatorId();

      if (selectedCreatorId && selectedCreatorId !== ALL_CREATORS_ID && !this.createMode()) {
        setTimeout(() => this.customModeCreatorId.set(selectedCreatorId));
      }
    });
  }

  private handleDeliverablesChanged(ctaForm: FormGroup<ICtaForm>): void {
    // Structural changes (FormArray push/removeAt of a content row, custom-value toggles) don't
    // mark the form dirty on their own, so the discard dialog is armed here on any deliverables
    // change - this subscription is the single place that sees every deliverable/child edit.
    this.armDiscardDialog(ctaForm);

    const currentGamePlayDeliverablesCount = this.getGamePlayDeliverablesCount(ctaForm);

    if (currentGamePlayDeliverablesCount <= this.previousGamePlayDeliverablesCount) {
      this.previousGamePlayDeliverablesCount = currentGamePlayDeliverablesCount;

      return;
    }

    if (ctaForm.controls.platformFeature.value === EPlatformFeature.twitchStream) {
      this.ctaNameGeneratorService.applyTwitchGamingStreamName(ctaForm, this.data.ctasValues ?? []);
    }

    this.previousGamePlayDeliverablesCount = currentGamePlayDeliverablesCount;
  }

  // Executions' own valueChanges also fires for nested expectedKpis edits (typing in a KPI row,
  // the auto-synced expectedKpis rows added on load), which must NOT arm the discard dialog - only
  // a structural execution add/remove changes the executions array length, so that's the arm
  // signal. KPI typing still arms via the regular dirty propagation (user input marks the tree
  // dirty), and KPI row adds are auto-sync, not user edits.
  private handleExecutionsChanged(ctaForm: FormGroup<ICtaForm>): void {
    const currentExecutionsCount = ctaForm.controls.executions.length;

    if (currentExecutionsCount === this.previousExecutionsCount) {
      return;
    }

    this.previousExecutionsCount = currentExecutionsCount;
    this.armDiscardDialog(ctaForm);
  }

  // Arms the discard dialog (ctaForm.markAsDirty) on the next macrotask instead of synchronously:
  // a structural add instantiates a new row component whose ngOnInit runs markAsPristine() (see
  // CustomCtaBaseDirective), and Angular's _updatePristine recomputes ancestors as pristine when
  // no child is dirty - a same-tick markAsDirty would be wiped and the dialog never armed
  // (verified 2026-08-07 against @angular/forms' _updatePristine). The timeout fires after that
  // component's ngOnInit, so the arm sticks. Coalesced: one pending arm is enough, since every
  // change that could wipe dirty schedules its own fresh arm.
  private armDiscardDialog(ctaForm: FormGroup<ICtaForm>): void {
    if (this.pendingDiscardArmTimeout !== null) {
      return;
    }

    this.pendingDiscardArmTimeout = setTimeout(() => {
      this.pendingDiscardArmTimeout = null;
      ctaForm.markAsDirty();
    });
  }

  private getGamePlayDeliverablesCount(ctaForm: FormGroup<ICtaForm>): number {
    return ctaForm.controls.deliverables
      .getRawValue()
      .filter((deliverable) => deliverable.type === EDeliverableType.gamePlay).length;
  }

  private shouldApplyInitialContentName(): boolean {
    if (this.data.ctaValues) {
      return false;
    }

    if (this.templateSelectedCreatorIds()) {
      return false;
    }

    return true;
  }

  private mapGlobalTemplatesToValues(templates: TGlobalTemplateApi[]): FormGroup<ICtaForm>['value'][] {
    return templates.map((template) => {
      const templateWithCampaignId: ICtaTemplateApi = {
        ...template,
        campaignId: template.campaignId ?? '',
      };
      const deliverables = CreatorsAndCtasService.getApiDeliverables(templateWithCampaignId);
      const executions = this.creatorsAndCtasService.getExecutions(templateWithCampaignId) ?? [];
      const ctaForm: FormGroup<ICtaForm> = this.creatorsAndCtasService.createCtaFormFromValues(
        templateWithCampaignId,
        deliverables,
        executions,
        false,
      );

      return ctaForm.value;
    });
  }

  private mergeCtaValues(
    campaignTemplates: FormGroup<ICtaForm>['value'][],
    globalTemplates: FormGroup<ICtaForm>['value'][],
  ): FormGroup<ICtaForm>['value'][] {
    const mergedTemplates: FormGroup<ICtaForm>['value'][] = [];
    const templateMap = new Map<string, FormGroup<ICtaForm>['value']>();

    campaignTemplates.forEach((ctaValue) => {
      if (!ctaValue.id) {
        mergedTemplates.push(ctaValue);

        return;
      }

      templateMap.set(ctaValue.id, ctaValue);
    });

    globalTemplates.forEach((ctaValue) => {
      if (!ctaValue.id) {
        mergedTemplates.push(ctaValue);

        return;
      }

      if (!templateMap.has(ctaValue.id)) {
        templateMap.set(ctaValue.id, ctaValue);
      }
    });

    mergedTemplates.push(...Array.from(templateMap.values()));

    return this.creatorsAndCtasService.sortCtaTemplates(mergedTemplates as TCtaTemplateSort[]);
  }

  private openConfirmDialog(flowDialogResult: IDeliverableFlowDialogResult): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.campaignForm.deliverableFlowConfirmDialog.continueEditing'),
        confirmText: this.translate.instant('pages.campaignForm.deliverableFlowConfirmDialog.discard'),
        message: this.translate.instant('pages.campaignForm.deliverableFlowConfirmDialog.message'),
        swapButtons: true,
        title: this.translate.instant('pages.campaignForm.deliverableFlowConfirmDialog.title'),
      },
      width: '400px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.closeDialog(flowDialogResult);
      }
    });
  }

  private applySelectedPlatformFeature(platformFeature: EPlatformFeature | null): void {
    const ctaForm = this.ctaForm();
    const previousPlatformFeature = ctaForm?.controls.platformFeature.value ?? null;
    const hasExistingForm = !!ctaForm;

    this.selectedPlatformFeature.set(platformFeature);

    if (!platformFeature) {
      return;
    }

    const ctaOption = CtaSelectService.allCtaOptions[platformFeature];

    ctaForm?.get('platformFeature')?.setValue(platformFeature, { emitEvent: false });
    ctaForm?.get('icon')?.setValue(ctaOption.icon, { emitEvent: false });

    if (hasExistingForm && previousPlatformFeature !== platformFeature && ctaForm) {
      this.ctaNameGeneratorService.applyPlatformFeatureName(ctaForm, this.data.ctasValues ?? [], platformFeature);
    }
  }

  private openPlatformChangeConfirmDialog(
    ctaForm: FormGroup<ICtaForm>,
    sourcePlatformFeature: EPlatformFeature,
    targetPlatformFeature: EPlatformFeature,
  ): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.campaignForm.platformChangeConfirmDialog.cancel'),
        confirmText: this.translate.instant('pages.campaignForm.platformChangeConfirmDialog.confirm'),
        message: this.translate.instant('pages.campaignForm.platformChangeConfirmDialog.message', {
          platformFeature: this.translate.instant(`shared.platformFeatures.${targetPlatformFeature}`),
        }),
        swapButtons: false,
        title: this.translate.instant('pages.campaignForm.platformChangeConfirmDialog.title'),
      },
      width: '400px',
    });

    dialogRef.afterClosed().subscribe((isConfirmed: boolean) => {
      if (!isConfirmed) {
        return;
      }

      void this.applyConfirmedPlatformChange(ctaForm, sourcePlatformFeature, targetPlatformFeature);
    });
  }

  private async applyConfirmedPlatformChange(
    ctaForm: FormGroup<ICtaForm>,
    sourcePlatformFeature: EPlatformFeature,
    targetPlatformFeature: EPlatformFeature,
  ): Promise<void> {
    const deliverableConfigurations = this.deliverableConfigurations();

    if (!deliverableConfigurations) {
      return;
    }

    await this.ctaTemplatePlatformChangeService.applyPlatformChange({
      ctaForm,
      deliverableConfigurations,
      sourcePlatformFeature,
      targetPlatformFeature,
    });
    this.applySelectedPlatformFeature(targetPlatformFeature);
    ctaForm.markAsDirty();
    this.checkStepConditions(this.selectedStepIndex());
  }

  private closeDialog(result: IDeliverableFlowDialogResult): void {
    this.customModeCreatorId.set(null);
    this.onModeChange(false);

    setTimeout(() => {
      this.dialogRef.close(result);
    });
  }

  private setExecutionValues(): void {
    const executionValues = this.ctaForm()?.get('executions')?.value as IExecution[];

    this.executionValues.set([]);

    setTimeout(() => {
      this.executionValues.set(
        executionValues?.filter(
          (execution) =>
            (this.customModeCreatorId() && execution.exclusiveTo === this.customModeCreatorId()) ||
            !execution.exclusiveTo,
        ) ?? [],
      );
    });
  }

  private setGamePlayValues(): void {
    const gamePlayValues =
      (this.ctaForm()
        ?.get('deliverables')
        ?.value?.filter((deliverable) => deliverable.type === EDeliverableType.gamePlay) as FormArray<
        FormGroup<IGamePlayDeliverableForm>
      >['value']) ?? [];

    this.gamePlayValues.set([]);

    setTimeout(() => {
      this.gamePlayValues.set(
        gamePlayValues?.filter(
          (execution) =>
            (this.customModeCreatorId() && execution.exclusiveTo === this.customModeCreatorId()) ||
            !execution.exclusiveTo,
        ) ?? [],
      );
    });
  }

  private isCtaFormEffectivelyValid(ctaForm: FormGroup<ICtaForm>): boolean {
    if (ctaForm.valid) {
      return true;
    }

    const allInvalidExecutionsAreUnselected = ctaForm.controls.executions.controls
      .filter((c) => c.invalid)
      .every((c) => !c.getRawValue().executionType);

    return allInvalidExecutionsAreUnselected && this.isFormValidExceptExecutions(ctaForm);
  }

  private hasRealExecutionsInForm(): boolean {
    return !!this.ctaForm()
      ?.getRawValue()
      ?.executions?.some((e) => !!e.executionType);
  }

  private isFormValidExceptExecutions(ctaForm: FormGroup<ICtaForm>): boolean {
    return (Object.keys(ctaForm.controls) as (keyof ICtaForm)[])
      .filter((key) => key !== 'executions')
      .every((key) => ctaForm.controls[key]?.valid ?? true);
  }

  private hasDeliverablesInForm(): boolean {
    return !!this.ctaForm()?.getRawValue()?.deliverables?.length;
  }

  private hasExecutionsInForm(): boolean {
    return !!this.ctaForm()?.getRawValue()?.executions?.length;
  }

  private getSanitizedCtaFormValues(
    ctaFormValues: FormGroup<ICtaForm>['value'],
    customModeCreatorId: string | TNil,
  ): FormGroup<ICtaForm>['value'] {
    return {
      ...ctaFormValues,
      executions:
        ctaFormValues.executions
          ?.filter((execution) => !!execution.executionType)
          .map((execution) => {
            return {
              ...execution,
              expectedKpis: this.getSanitizedExpectedKpis(execution.expectedKpis, customModeCreatorId),
            };
          }) ?? [],
    };
  }

  private getSanitizedExpectedKpis(expectedKpis: unknown, customModeCreatorId: string | TNil): IExpectedKpi[] {
    if (!Array.isArray(expectedKpis)) {
      return [];
    }

    return (expectedKpis as Array<Partial<IExpectedKpi>>)
      .filter((expectedKpi: Partial<IExpectedKpi>) => {
        if (expectedKpi.exclusiveTo) {
          // A creator-owned row's value IS its expectedValue. An emptied row must not be
          // submitted: its globalCtaValues still hold the pre-clear value (initialized from the
          // row's own value at mount), so hasPersistedValue would keep it and the backend would
          // reject an exclusive row without a value. The empty row stays in the form as an
          // editing surface - the payload simply omits it (the backend then deletes the row).
          return ExecutionExpectedKpisService.isExpectedValueSet(expectedKpi.expectedValue);
        }

        // A deleted-marker row (expected value cleared in custom mode) carries no persisted value,
        // but must be kept so the marker reaches the backend - dropping it would leave the old
        // value intact. Checked for any creator's marker, not just the currently active custom
        // mode creator - switching custom mode before saving must not drop another creator's
        // marker from the payload.
        if (
          (expectedKpi.customCtaValues as ICustomCtaValue[] | null | undefined)?.some(
            (customCtaValue) => customCtaValue.valueName === null && customCtaValue.status === EOverwriteStatus.deleted,
          )
        ) {
          return true;
        }

        return this.customValuesService.hasPersistedValue(expectedKpi, 'expectedValue', (value: unknown): boolean =>
          ExecutionExpectedKpisService.isExpectedValueSet(value as number | string | TNil),
        );
      })
      .map((expectedKpi) => this.restoreGlobalExpectedKpiValue(expectedKpi, customModeCreatorId)) as IExpectedKpi[];
  }

  // While editing in custom mode the row's expectedValue control holds the creator's own value
  // (switchToCustomMode applies it to the shared row), but the saved row must carry the GLOBAL
  // value - the creator's value lives in customCtaValues. Submitting the custom value as the row
  // value would overwrite the shared goal for every creator and the global CTA template.
  private restoreGlobalExpectedKpiValue(
    expectedKpi: Partial<IExpectedKpi>,
    customModeCreatorId: string | TNil,
  ): Partial<IExpectedKpi> {
    if (!customModeCreatorId || expectedKpi.exclusiveTo) {
      return expectedKpi;
    }

    const globalValue = expectedKpi.globalCtaValues?.find((value) => value.valueName === 'expected_value');

    if (!globalValue) {
      return expectedKpi;
    }

    return { ...expectedKpi, expectedValue: globalValue.value === null ? null : Number(globalValue.value) };
  }
}
